import React, { useEffect, useMemo, useState } from 'react';
import { fetchDevicesByCustomer } from '../services/devicesService';
import { updateServiceRecord } from '../services/serviceRecordsService';

const getErrorMessage = (err, fallback) => {
  const message = err?.message || err?.mesaj || err?.data?.mesaj;
  return message || fallback;
};

const deviceIdOf = (device) => String(device?.id || device?._id || '');

const ServisKaydiCihazDuzenleModal = ({
  isOpen,
  servisKaydiId,
  musteriId,
  islemTipi,
  initialCihazIds = [],
  initialCihazSecilmedi = false,
  initialCihazAciklama = '',
  onClose,
  onSuccess,
}) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deviceNotSelected, setDeviceNotSelected] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [cihazAciklama, setCihazAciklama] = useState('');

  const isKurulum = islemTipi === 'Kurulum';
  const isAriza = islemTipi === 'Arıza';
  const singleSelect = isAriza;

  useEffect(() => {
    if (!isOpen || !musteriId || isKurulum) return;
    setError('');
    setLoading(true);
    fetchDevicesByCustomer(musteriId, { page: 1, limit: 100 })
      .then((res) => {
        const list = res?.veri?.cihazlar || res?.veri?.devices || res?.veri || [];
        setDevices(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        setError(getErrorMessage(err, 'Cihazlar yüklenemedi'));
        setDevices([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, musteriId, isKurulum]);

  useEffect(() => {
    if (!isOpen) return;
    const ids = (initialCihazIds || []).map((x) =>
      typeof x === 'object' ? String(x.id || x._id || '') : String(x),
    ).filter(Boolean);
    setSelectedIds(ids);
    setDeviceNotSelected(Boolean(initialCihazSecilmedi));
    setCihazAciklama(initialCihazAciklama || '');
    setError('');
  }, [isOpen, initialCihazIds, initialCihazSecilmedi, initialCihazAciklama]);

  const toggleDevice = (id) => {
    if (deviceNotSelected) return;
    if (singleSelect) {
      setSelectedIds((prev) => (prev.includes(id) ? [] : [id]));
      return;
    }
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const canSubmit = useMemo(() => {
    if (isKurulum) return false;
    if (deviceNotSelected) {
      return String(cihazAciklama || '').trim().length > 0;
    }
    return selectedIds.length > 0;
  }, [isKurulum, deviceNotSelected, cihazAciklama, selectedIds.length]);

  if (!isOpen) return null;

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!servisKaydiId || !canSubmit) return;
    if (isAriza && !deviceNotSelected && selectedIds.length > 1) {
      setError('Arıza kaydında yalnızca bir cihaz seçilebilir');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await updateServiceRecord(servisKaydiId, {
        cihazSecilmedi: deviceNotSelected,
        cihazIds: deviceNotSelected ? [] : selectedIds,
        cihazAciklama: deviceNotSelected ? String(cihazAciklama).trim() : '',
      });
      window.showNotification?.('success', 'Cihaz bilgisi güncellendi');
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Cihaz güncellenemedi'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="modal-overlay"
      onClick={handleClose}
    >
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cihazı düzenle</h2>
          <button type="button" className="modal-close-btn" onClick={handleClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <form className="modal-body" onSubmit={handleSubmit}>
          {isKurulum ? (
            <p style={{ margin: 0, color: 'var(--text-gray)', fontSize: 14 }}>
              Kurulum kayıtlarında cihaz bu ekrandan değiştirilmez.
            </p>
          ) : (
            <>
              <label className="form-field" style={{ marginBottom: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={deviceNotSelected}
                    onChange={(e) => {
                      setDeviceNotSelected(e.target.checked);
                      if (e.target.checked) setSelectedIds([]);
                    }}
                  />
                  Cihaz seçilmedi
                </span>
              </label>
              {!deviceNotSelected && (
                <div style={{ marginBottom: 12 }}>
                  {loading ? (
                    <div style={{ fontSize: 14, color: 'var(--text-gray)' }}>Cihazlar yükleniyor…</div>
                  ) : devices.length === 0 ? (
                    <div style={{ fontSize: 14, color: 'var(--text-gray)' }}>Kayıtlı cihaz yok</div>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 220, overflow: 'auto' }}>
                      {devices.map((device) => {
                        const id = deviceIdOf(device);
                        if (!id) return null;
                        const checked = selectedIds.includes(id);
                        const name =
                          device.icerikAdi ||
                          device.urunAdi ||
                          device.cihazAdi ||
                          device.ad ||
                          '—';
                        const seri = device.seriNumarasi || device.seriNo || '—';
                        return (
                          <li key={id} style={{ marginBottom: 8 }}>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 8,
                                cursor: 'pointer',
                                padding: 8,
                                borderRadius: 8,
                                border: checked ? '1px solid #2563eb' : '1px solid var(--border-color)',
                                background: checked ? '#eff6ff' : '#fff',
                              }}
                            >
                              <input
                                type={singleSelect ? 'radio' : 'checkbox'}
                                name="servis-kaydi-cihaz"
                                checked={checked}
                                onChange={() => toggleDevice(id)}
                              />
                              <span>
                                <div style={{ fontWeight: 500 }}>{name}</div>
                                <div style={{ fontSize: 12, color: 'var(--text-gray)' }}>{seri}</div>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
              {deviceNotSelected && (
                <label className="form-field">
                  <span>Cihaz açıklaması</span>
                  <textarea
                    value={cihazAciklama}
                    onChange={(e) => setCihazAciklama(e.target.value)}
                    rows={3}
                    maxLength={500}
                    required
                  />
                </label>
              )}
            </>
          )}
          {error && (
            <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{error}</div>
          )}
          <div className="modal-footer" style={{ marginTop: 16 }}>
            <button type="button" className="secondary-btn" onClick={handleClose} disabled={submitting}>
              İptal
            </button>
            {!isKurulum && (
              <button type="submit" className="primary-btn" disabled={submitting || !canSubmit}>
                {submitting ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServisKaydiCihazDuzenleModal;
